// AddMultipleTimeEntries.js
import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "reactstrap";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AppModal from "../../Common/AppModal";
import Select from "../../Common/AppSelect";
import moment from "moment";
import { AppDateTimePicker } from "../../Common/AppDateTimePicker";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import JobSearchList from "../../Common/JobSearchList";
import { timeFormatDateAndTime } from "../../Common/HandleDateTime";
import {
  getTimeEntriesList,
  getTimeEntryTypeList,
  timeEntryCreate,
  timeEntryCreateSuccess,
} from "../../../store/timeEntries/action";
import { searchProductsAllowedTimeTracking } from "../../../store/products/action";
import { toast } from "react-toastify";

const makeEmptyEntry = (Staff_Id) => ({
  lid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  Staff_Id: Staff_Id ? String(Staff_Id) : "",
  BillableType: "1", // 1 = billable, 2 = non-billable
  Job_Id: "",
  Customer_Id: null,
  Job_Title: "",
  Customer_FullName: "",
  TimeEntryStartDateTime: timeFormatDateAndTime(new Date()),
  TimeEntryEndDateTime: timeFormatDateAndTime(new Date()),
  Notes: "",
  Product_Id: "0",
  TimeEntryTypeUnbillable_Id: "",
  // per-row job search UI state
  jobSearchQuery: "",
  jobResetSearchQuery: false,
  selectedJob: null,
});

export default function AddMultipleTimeEntries({ modal, setModal, toggle }) {
  const dispatch = useDispatch();

  const Staff_Id = JSON.parse(localStorage.getItem("authUser"))?.get_staff_data
    ?.Staff_Id;

  const { timeEntryTypeList } = useSelector((state) => ({
    timeEntryTypeList:
      state.TimeEntries.timeEntryTypes?.timeentrytypeunbillable_list,
  }));

  const { searchProductsAllowedTimeTrackingList } = useSelector((state) => ({
    searchProductsAllowedTimeTrackingList:
      state.Products.searchProductsAllowedTimeTracking?.product_list,
  }));

  // load supporting lists once
  useEffect(() => {
    if (isEmpty(timeEntryTypeList)) {
      dispatch(getTimeEntryTypeList());
    }
    if (isEmpty(searchProductsAllowedTimeTrackingList)) {
      dispatch(
        searchProductsAllowedTimeTracking([
          {
            product_data: {
              AllowTimeTracking: 1,
            },
          },
        ])
      );
    }
  }, []);

  const [entries, setEntries] = useState([makeEmptyEntry(Staff_Id)]);

  const addEntry = () => {
    setEntries((prev) => {
      const newEntry = makeEmptyEntry(Staff_Id);

      if (!prev.length) {
        // first row – just use defaults
        return [...prev, newEntry];
      }

      const last = prev[prev.length - 1];

      const populatedEntry = {
        ...newEntry,
        TimeEntryStartDateTime: last.TimeEntryEndDateTime,
        TimeEntryEndDateTime: last.TimeEntryEndDateTime,
      };

      return [...prev, populatedEntry];
    });
  };

  const removeEntry = (lid) => {
    setEntries((prev) => prev.filter((e) => e.lid !== lid));
  };

  const updateEntry = (lid, patch) => {
    setEntries((prev) =>
      prev.map((e) => (e.lid === lid ? { ...e, ...patch } : e))
    );
  };

  const handleSelectJobForRow = (lid, jobObject) => {
    updateEntry(lid, {
      Job_Id: jobObject?.Id ?? "",
      Job_Title: jobObject?.Title ?? "",
      Customer_FullName: jobObject?.Customer_FullName ?? "",
      Customer_Id: jobObject?.Customer_Id ?? null,
      selectedJob: jobObject || null,
    });
  };

  const handleSubmit = () => {
    // Task 24721. There was no check here at all, so a row ending before it
    // started went to the server - and /timeentry/create returns on the FIRST
    // bad row, after the rows before it have already been inserted, leaving a
    // half-saved batch. Caught here instead, before anything is sent. Mirrors
    // the guard AddJobTimeEntry already has.
    const invalid = entries.find(
      (e) =>
        e.TimeEntryStartDateTime &&
        e.TimeEntryEndDateTime &&
        !moment(e.TimeEntryEndDateTime).isAfter(
          moment(e.TimeEntryStartDateTime)
        )
    );
    if (invalid) {
      toast.error("Please make sure End is after Start for every entry");
      return;
    }

    // build payload exactly as requested
    const payload = entries.map((e) => ({
      BillableType: e.BillableType,
      Staff_Id: String(e.Staff_Id || Staff_Id || ""),
      TimeEntryStartDateTime: e.TimeEntryStartDateTime,
      TimeEntryEndDateTime: e.TimeEntryEndDateTime,
      Notes: e.Notes,
      Product_Id: e.Product_Id || "0",
      Customer_Id: e.Customer_Id ?? null,
      Job_Id: e.BillableType === "1" ? e.Job_Id || null : null,
      TimeEntryTypeUnbillable_Id:
        e.BillableType === "2" ? e.TimeEntryTypeUnbillable_Id || null : null,
    }));

    dispatch(timeEntryCreate(payload));
  };

  const { timeEntryCreateSuccessAPI, timeEntryCreateLoading } = useSelector(
    (state) => ({
      timeEntryCreateSuccessAPI: state.TimeEntries.timeEntryCreateSuccess,
      timeEntryCreateLoading: state.TimeEntries.timeEntryCreateLoading,
    })
  );

  useEffect(() => {
    if (isEmpty(timeEntryCreateSuccessAPI)) return;
    if (timeEntryCreateSuccessAPI.status === 200) {
      toast.success(timeEntryCreateSuccessAPI.message);
      setEntries([makeEmptyEntry(Staff_Id)]);
      setModal(false);
      dispatch(getTimeEntriesList());
    } else {
      toast.error(timeEntryCreateSuccessAPI.message);
    }
    dispatch(timeEntryCreateSuccess([]));
  }, [timeEntryCreateSuccessAPI]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title="Create Multiple Time Entries"
      icon={<AccessTimeOutlinedIcon />}
      headerAccent="indigo"
      size="md"
      loading={timeEntryCreateLoading}
      loadingText="Creating…"
      submitLabel="Create Multiple Entries"
      submitColor="primary"
      onSubmit={handleSubmit}
    >
      <Row>
          <Col>
            {entries.map((entry, index) => (
              <Card className="mb-3 p-3" key={entry.lid}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Entry #{index + 1}</strong>
                  {entries.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeEntry(entry.lid)}
                    >
                      <i className="fa-regular fa-trash-can me-1" />
                      Remove
                    </button>
                  )}
                </div>

                {/* Billable toggle */}
                <div className="mb-2">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`billable-${entry.lid}`}
                      checked={entry.BillableType === "1"}
                      onChange={() =>
                        updateEntry(entry.lid, {
                          BillableType: "1",
                          TimeEntryTypeUnbillable_Id: "",
                        })
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`billable-${entry.lid}`}
                    >
                      Billable
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`nonbillable-${entry.lid}`}
                      checked={entry.BillableType === "2"}
                      onChange={() =>
                        updateEntry(entry.lid, {
                          BillableType: "2",
                          Job_Id: "",
                          Job_Title: "",
                          Customer_FullName: "",
                          Customer_Id: null,
                        })
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`nonbillable-${entry.lid}`}
                    >
                      None Billable
                    </label>
                  </div>
                </div>

                {/* Job / Non-billable reason */}
                {entry.BillableType === "1" ? (
                  <div className="mb-2">
                    <JobSearchList
                      handleSelectJobFromSearchResults={(job) =>
                        handleSelectJobForRow(entry.lid, job)
                      }
                      selectedJob={entry.selectedJob}
                      dropDownTimer={true}
                      searchQuery={entry.jobSearchQuery}
                      resetSearchQuery={entry.jobResetSearchQuery}
                      setSearchQuery={(q) =>
                        updateEntry(entry.lid, { jobSearchQuery: q })
                      }
                    />
                  </div>
                ) : (
                  <div className="mb-2">
                    <Select
                      isClearable
                      className="job-select3"
                      placeholder={"Select Non-Billable Reason..."}
                      value={
                        entry.TimeEntryTypeUnbillable_Id
                          ? {
                              label:
                                "#" +
                                entry.TimeEntryTypeUnbillable_Id +
                                ", " +
                                timeEntryTypeList?.find(
                                  (t) =>
                                    t.Id === entry.TimeEntryTypeUnbillable_Id
                                )?.Name,
                            }
                          : null
                      }
                      onChange={(opt) =>
                        updateEntry(entry.lid, {
                          TimeEntryTypeUnbillable_Id: opt?.id || "",
                        })
                      }
                      options={
                        !isEmpty(timeEntryTypeList)
                          ? timeEntryTypeList.map((item) => ({
                              label: `#${item.Id}, ${item.Name}`,
                              value: item.Id,
                              id: item.Id,
                            }))
                          : []
                      }
                    />
                  </div>
                )}

                {/* Product */}
                {entry.BillableType === "1" && (
                  <div className="mb-2">
                    <Select
                      isClearable
                      className="job-select3"
                      placeholder={"Select Product..."}
                      value={
                        entry.Product_Id && entry.Product_Id !== "0"
                          ? {
                              label:
                                "#" +
                                entry.Product_Id +
                                ", " +
                                searchProductsAllowedTimeTrackingList?.find(
                                  (p) => p.Id == entry.Product_Id
                                )?.Name,
                            }
                          : null
                      }
                      onChange={(opt) =>
                        updateEntry(entry.lid, {
                          Product_Id: opt?.id ? String(opt.id) : "0",
                        })
                      }
                      options={
                        !isEmpty(searchProductsAllowedTimeTrackingList)
                          ? searchProductsAllowedTimeTrackingList.map(
                              (item) => ({
                                label: `#${item.Id}, ${item.Name}`,
                                value: item.Id,
                                id: item.Id,
                              })
                            )
                          : []
                      }
                    />
                  </div>
                )}

                {/* Start / End */}
                <Row className="mb-2">
                  <Col md={6}>
                    <label className="form-label">Start</label>
                    <AppDateTimePicker
                      value={
                        entry.TimeEntryStartDateTime
                          ? moment(entry.TimeEntryStartDateTime)
                          : null
                      }
                      onChange={(val) => {
                        const jsDate = val && val.isValid() ? val.toDate() : null;
                        updateEntry(entry.lid, {
                          TimeEntryStartDateTime: timeFormatDateAndTime(jsDate),
                        });
                      }}
                    />
                  </Col>
                  <Col md={6}>
                    <label className="form-label">End</label>
                    <AppDateTimePicker
                      // Task 24721: greys out anything before the start, so the
                      // backwards value is not pickable in the first place.
                      minDateTime={
                        entry.TimeEntryStartDateTime
                          ? moment(entry.TimeEntryStartDateTime)
                          : undefined
                      }
                      value={
                        entry.TimeEntryEndDateTime
                          ? moment(entry.TimeEntryEndDateTime)
                          : null
                      }
                      onChange={(val) => {
                        const jsDate = val && val.isValid() ? val.toDate() : null;
                        updateEntry(entry.lid, {
                          TimeEntryEndDateTime: timeFormatDateAndTime(jsDate),
                        });
                      }}
                    />
                  </Col>
                </Row>

                {/* Notes */}
                <div className="mb-2">
                  <label className="form-label">Notes</label>
                  <textarea
                    rows={2}
                    className="form-control"
                    placeholder="Type your notes here..."
                    value={entry.Notes}
                    onChange={(e) =>
                      updateEntry(entry.lid, { Notes: e.target.value })
                    }
                  />
                </div>
              </Card>
            ))}
          </Col>
        </Row>

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={addEntry}
        >
          <i className="fa-solid fa-plus me-1" />
          Add another entry
        </button>
    </AppModal>
  );
}
